import type { Vec2 } from '../../geo/types';
import type { LifeClearance } from './routes';
import type { LifeStep } from './types';
import { routeClear } from './routes';
import { corners, heading, travel } from './itinerary';
import { pedestrianPath } from './pedestrianPath';
import { distance } from './streetGraph';

const BODY_RADIUS = 0.48;
const STOP_GAP = 1.12;

function stoppingPlace(
  at: Vec2,
  neighbour: Vec2,
  preferred: number,
  clearance: LifeClearance,
  booked: readonly Vec2[]
): Vec2 | null {
  const facing = heading(at, neighbour);
  const candidates: Vec2[] = [preferred, -preferred, 0].map((side) => [
    at[0] + Math.sin(facing) * side,
    at[1] - Math.cos(facing) * side,
  ]);
  for (const radius of [1.2, 2.4, 3.6, 4.8])
    for (const angle of [0, 1, 0.5, -0.5, 0.25, -0.25, 0.75, -0.75]) {
      const direction = facing + angle * Math.PI;
      candidates.push([at[0] + Math.cos(direction) * radius, at[1] + Math.sin(direction) * radius]);
    }
  return (
    candidates.find(
      (position) =>
        booked.every((other) => distance(position, other) >= STOP_GAP) &&
        routeClear({ a: position, b: neighbour, radius: BODY_RADIUS }, clearance)
    ) ?? null
  );
}

/**
 * Shared graph junctions need separate halting places so sentries cannot trap returning patrols.
 */
export function planWatchRound(
  path: readonly Vec2[],
  clearance: LifeClearance,
  lane: number,
  speed: number,
  booked: Vec2[],
  stations: readonly Vec2[]
): LifeStep[] | null {
  if (path.length < 3) return null;
  const standing: LifeClearance = {
    clear: (position, radius) =>
      clearance.clear(position, radius) &&
      stations.every((other) => distance(position, other) >= radius + 0.34 + 0.04),
  };
  const start = stoppingPlace(path[0]!, path[1]!, lane, standing, booked);
  if (!start) return null;
  const end = stoppingPlace(path.at(-1)!, path.at(-2)!, -lane, standing, [...booked, start]);
  if (!end) return null;
  const points = [start, ...path.slice(1, -1), end];
  const out = corners(pedestrianPath(points, standing, lane));
  const back = corners(pedestrianPath([...points].reverse(), standing, lane));
  const steps: LifeStep[] = [
    {
      from: start,
      to: start,
      seconds: 40,
      action: 'wait',
      heading: heading(start, out[1]!),
      prop: 'lantern',
    },
  ];
  travel(steps, out, speed, 'lantern');
  steps.push({
    from: end,
    to: end,
    seconds: 30,
    action: 'wait',
    heading: heading(end, back[1]!),
    prop: 'lantern',
  });
  travel(steps, back, speed, 'lantern');
  if (
    steps.some(
      (step) =>
        step.action === 'walk' &&
        !routeClear({ a: step.from, b: step.to, radius: BODY_RADIUS }, standing)
    )
  )
    return null;
  booked.push(start, end);
  return steps;
}
